/**
 * Dynamic elements — ad↔landing-page congruence (DYNAMIC_ELEMENTS.md).
 *
 * Two render-time mechanisms, both driven by URL query params:
 *   - Token substitution — `{{param}}` placeholders inside block strings.
 *   - Variant selection — a `dynamic` block picks a block-set by a param value.
 *
 * Only params DECLARED in `config.dynamic.params` are ever read; that map is
 * the allow-list. Every declared param has a default, so a click with no
 * params (organic / direct) always renders the authored defaults.
 *
 * Pure functions, no Preact dependency — `window.location.search` is injected
 * by the caller so the module is testable.
 */
import type { Block, DynamicBlock, DynamicConfig, DynamicParam } from "../types";

/** An unsubstituted Meta macro (e.g. "{{campaign.name}}") arrives literal. */
const LITERAL_MACRO = /^\{\{.*\}\}$/;
/** A `{{param}}` token — param names are word characters only. */
const TOKEN = /\{\{\s*(\w+)\s*\}\}/g;

function applyCase(v: string, mode: DynamicParam["case"]): string {
  switch (mode) {
    case "upper":
      return v.toUpperCase();
    case "lower":
      return v.toLowerCase();
    case "title":
      return v.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    case "sentence":
      return v.charAt(0).toUpperCase() + v.slice(1);
    default:
      return v;
  }
}

/** Resolve one param's raw value through allow → max_length → case. */
function resolveOne(param: DynamicParam, raw: string | null): string {
  if (raw == null || raw === "" || LITERAL_MACRO.test(raw)) return param.default;
  if (param.allow) {
    let ok = false;
    try {
      ok = new RegExp(param.allow).test(raw);
    } catch {
      ok = false; // a malformed allow regex rejects everything → default
    }
    if (!ok) return param.default;
  }
  const cap = param.max_length ?? 200;
  const clipped = raw.length > cap ? raw.slice(0, cap) : raw;
  return applyCase(clipped, param.case ?? "none");
}

/**
 * Resolve every declared param against the URL query string, falling back to
 * `dynamic.preview` then the param's `default`. Intended to be computed once
 * at mount. The returned map is frozen.
 */
export function resolveParams(
  cfg: DynamicConfig | undefined,
  search: string,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!cfg) return out;
  const usp = new URLSearchParams(search);
  for (const [name, param] of Object.entries(cfg.params)) {
    const fromUrl = usp.get(name);
    const raw =
      fromUrl != null && fromUrl !== "" ? fromUrl : (cfg.preview?.[name] ?? null);
    out[name] = resolveOne(param, raw);
  }
  Object.freeze(out);
  return out;
}

const HTML_ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => HTML_ESCAPE[c] ?? c);
}

/**
 * Substitute `{{param}}` tokens in a string against a resolved map. A token
 * whose param is not declared is left literal. When `escape` is true the
 * substituted value is HTML-escaped — REQUIRED for strings bound via
 * `dangerouslySetInnerHTML`; pass false for values rendered as JSX children
 * (Preact escapes those itself — escaping twice would show `&amp;`).
 */
export function substituteTokens(
  input: string,
  resolved: Record<string, string>,
  escape: boolean,
): string {
  if (input.indexOf("{{") === -1) return input;
  return input.replace(TOKEN, (whole, name: string) => {
    if (!(name in resolved)) return whole;
    const v = resolved[name]!;
    return escape ? escapeHtml(v) : v;
  });
}

/** Pick the block-set a `dynamic` block renders, given the resolved map. */
export function selectVariant(
  block: DynamicBlock,
  resolved: Record<string, string>,
): Block[] {
  const key = resolved[block.param];
  if (key != null && Object.prototype.hasOwnProperty.call(block.cases, key)) {
    return block.cases[key]!;
  }
  return block.default;
}
